import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import reblieLogo from "../../assets/images/CrimeRisk.AI Final Logo.png";
import { palette, themeWidth } from "../../themes/theme";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PrintIcon from "@mui/icons-material/Print";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import theme from "../../themes/theme";
import { SearchBox } from "@mapbox/search-js-react";
import "./Map.css";
import AdditionalLayers from "./AdditionalLayers";
import ExportSettings from "./ExportSettings";
import LocationsAndTradeAreas from "./LocationsAndTradeAreas";
import BatchUpload from "./BatchUpload";
import { CustomMarkerClass } from "../../component/MapSettings/utilities/CustomMarker";
import { MapLayoutWidth } from "../../utils/helper";
import CrimeRiskReports from "./CrimeRiskReports";
import CrimeRiskSettingModal from "../../component/Modal/CrimeRiskSettingModal";
import { showLoader } from "../../redux/slices/loaderSlice";
import MapStyles from "./MapStyles";
import MapEditingTools from "./MapEditingTools";
import EnvironmentalRiskReports from "./EnvironmentalRiskReports";
import { updateShadingSettings } from "../../redux/slices/mapSlice";
import { StyledSwitch } from "../../component/ImageEditor/StyledComps";

const FeatureTypesZoomLevel = {
  country: 3,
  region: 10,
  place: 10,
  locality: 12,
  neighborhood: 14,
  address: 15,
  poi: 16,
};

const shadingLevel = {
  continents: "Counties",
  blocks: "Block",
  blockGroups: "Block Group",
  FS: "Forward Sortation Area",
  DA: "Dissemination Area",
};

const MapLayout = ({
  setCountryToggle,
  countryToggle,
  hasCanada,
  canToggleCountry,
  mapRef,
  setMapLayout,
  mapLayoutState,
  mapboxglRef,
  mapRoadLayer,
  mapFeatureLayer,
  exportSettingsData,
  setExportSettingsData,
  exportChanges,
  markers,
  setMarkers,
  lastClickButtonRef,
  resetAllOnceChanged,
  resetActiveId,
  markerRef,
  setShowOpacityControl,
  activeLayerRef,
  setPolygonSelected,
  createPolygon,
  setMapToolBarOptionFlags,
  resetMapToolbarOptionFlags,
  setShapeSettings,
}) => {
  const navigate = useNavigate();
  const radiusSetting = useSelector(
    (state) => state.mapSettings.locationTradeAreaSetting.radiusSetting
  );
  const allTradeAreas = useSelector((state) => state.map.tradeAreas);
  const activeCrimeType = useSelector((state) => state.map.activeCrimeType);
  const activeEnvType = useSelector((state) => state.map.activeEnvType);
  const dispatch = useDispatch();

  const [searchValue, setSearchValue] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const checkPreferedColorScheme = () => {
    const userColorScheme = JSON.parse(
      localStorage.getItem("preferedColorPalette")
    );
    if (userColorScheme) {
      dispatch(
        updateShadingSettings({
          ...userColorScheme,
          useGradings:
            userColorScheme.legendColorSchemeData?.length < 13
              ? false
              : userColorScheme.useGradings,
        })
      );
    }
  };

  useEffect(() => {
    checkPreferedColorScheme();
  }, []);
  const HandleCrimeRiskSettingSave = () => {
    dispatch(
      showLoader({
        showLoader: true,
        showBackDrop: true,
      })
    );
    setTimeout(() => {
      setIsModalOpen(false);
      dispatch(
        showLoader({
          showLoader: false,
          showBackDrop: false,
        })
      );
    }, 1000);
  };

  const handleExportMenuClick = () => {
    markers.forEach((d) => {
      d.marker.setDraggable(false);
    });
    setMapLayout(true);
  };

  const handleCloseExport = () => {
    markers.forEach((d) => {
      if (d.type !== "marker" && d.type !== "pointer") {
        d.marker.setDraggable(true);
      }
    });
    setMapLayout(false);
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <Box
        sx={{
          position: "relative",
          zIndex: 3,
          width:
            MapLayoutWidth > themeWidth[MapLayoutWidth]
              ? themeWidth[MapLayoutWidth]
              : MapLayoutWidth,
          minWidth:
            MapLayoutWidth > themeWidth[MapLayoutWidth]
              ? themeWidth[MapLayoutWidth]
              : MapLayoutWidth,
          background: palette.background.light,
          height: "100vh",
          p: "1.2vh 0.6vw",
          borderRight: `2px solid ${palette.primary.main}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            // gap: "0.7vw",
            pb: "1vh",
            alignItems: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              cursor: "pointer",
              justifyContent: "center",
              width: "100%",
            }}
            onClick={() => {
              navigate("/map");
            }}
          >
            <img src={reblieLogo} alt="Reblie" style={{ width: "280px" }} />
          </div>
        </Box>

        {!mapLayoutState ? (
          <>
            <Box
              sx={{
                height: "calc(100vh - 110px)",
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              <Box
                sx={{
                  pt: "1vh",
                  display: "flex",
                  flexDirection: "column",
                  gap: "1vh",
                  pb: "4vh",
                  mr: "10px",
                }}
              >
                <Box className="mapbox-search" sx={{ mb: "1vh" }}>
                  <SearchBox
                    accessToken={process.env.REACT_APP_MAP_BOX_API_KEY}
                    placeholder="Find Location"
                    options={{
                      types:
                        "country,region,postcode,district,place,locality,address,poi,street",
                      country: hasCanada ? "us,ca" : "us",
                      // bbox: RestrictedMapBbox,
                    }}
                    value={searchValue}
                    onChange={(val) => setSearchValue(val)}
                    onRetrieve={(res) => {
                      setSearchValue("");
                      resetActiveId();
                      resetAllOnceChanged();
                      const value = res.features[0].properties;
                      const name = value.full_address
                        ? value.full_address
                        : value.name;
                      const id = `${value.coordinates.longitude} ${value.coordinates.latitude} ${name}`;

                      const foundTradeArea = allTradeAreas.find(
                        (d) => d.id === id
                      );
                      if (foundTradeArea) {
                        mapRef.flyTo({
                          center: [
                            value.coordinates.longitude,
                            value.coordinates.latitude,
                          ],
                          zoom:
                            value.feature_type &&
                            value.feature_type in FeatureTypesZoomLevel
                              ? FeatureTypesZoomLevel[value.feature_type]
                              : 14,
                        });
                        return;
                      }
                      CustomMarkerClass.AddMarkerToPolygon({
                        id,
                        coordinates: value.coordinates,
                        dispatch,
                        mapRef,
                        name,
                        radiusSetting,
                        lastClickButton: lastClickButtonRef,
                        resetAllOnceChanged,
                        resetActiveId,
                        markerRef,
                      });
                      setMarkers(markerRef.current);

                      mapRef.flyTo({
                        center: [
                          value.coordinates.longitude,
                          value.coordinates.latitude,
                        ],
                        zoom:
                          value.feature_type &&
                          value.feature_type in FeatureTypesZoomLevel
                            ? FeatureTypesZoomLevel[value.feature_type]
                            : 14,
                      });
                    }}
                    mapboxgl={mapboxglRef}
                    theme={{
                      variables: {
                        colorBackground: theme.palette.background.light,
                        colorText: theme.palette.text.dark,
                        colorBackdrop: "red",
                        colorBackgroundHover: "grey",
                      },
                    }}
                  />
                </Box>
                <Accordion
                  defaultExpanded
                  sx={{
                    background: palette.background.light,
                    color: palette.text.dark,
                    border: `1px solid ${palette.primary.main}`,
                    boxShadow: "none",
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreIcon sx={{ color: palette.primary.main }} />
                    }
                    sx={{ fontWeight: "bold" }}
                  >
                    Location & Trade Areas
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      maxHeight: 130,
                      overflowY: "auto",
                      overflowX: "hidden",
                    }}
                  >
                    <Stack
                      width="100%"
                      direction="row"
                      justifyContent="around"
                      point
                      spacing={2}
                      paddingBottom={2}
                    >
                      <Typography
                        variant="46"
                        fontWeight="400"
                        sx={{ color: "#333", fontSize: "16px" }}
                      >
                        CANADA
                      </Typography>
                      <FormControlLabel
                        control={
                          <StyledSwitch
                            checked={countryToggle}
                            disabled={!canToggleCountry}
                            sx={{
                              cursor: canToggleCountry
                                ? "pointer"
                                : "not-allowed",
                            }}
                            onChange={(e) => {
                              if (canToggleCountry) {
                                setCountryToggle(e.target.checked);
                              }
                            }}
                          />
                        }
                        label=""
                      />
                      <Typography
                        variant="46"
                        fontWeight="400"
                        sx={{ color: "#333", fontSize: "16px" }}
                      >
                        USA
                      </Typography>
                    </Stack>
                    <LocationsAndTradeAreas
                      markers={markers}
                      setMarkers={setMarkers}
                      markerRef={markerRef}
                      mapRef={mapRef}
                      resetAllOnceChanged={resetAllOnceChanged}
                    />
                  </AccordionDetails>
                </Accordion>

                <Accordion
                  defaultExpanded
                  sx={{
                    background: palette.background.light,
                    color: theme.palette.text.dark,
                    border: `1px solid ${palette.primary.main}`,
                    boxShadow: "none",
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreIcon sx={{ color: palette.primary.main }} />
                    }
                    sx={{ fontWeight: "bold" }}
                  >
                    Crime & Environment Risk
                  </AccordionSummary>
                  <AccordionDetails>
                    <Accordion
                      sx={{
                        background: palette.background.light,
                        color: theme.palette.text.dark,
                        border: `1px solid ${palette.primary.main}`,
                        boxShadow: "none",
                        marginBottom: "8px",
                      }}
                    >
                      <AccordionSummary
                        expandIcon={
                          <ExpandMoreIcon
                            sx={{ color: palette.primary.main }}
                          />
                        }
                        sx={{
                          fontWeight: "bold",
                          background: activeCrimeType
                            ? palette.primary.hover
                            : "transparent",
                        }}
                      >
                        {`Crime Risk ${
                          activeCrimeType
                            ? `(${shadingLevel[activeLayerRef.current.name]})`
                            : ""
                        }`}
                      </AccordionSummary>
                      <AccordionDetails>
                        <CrimeRiskReports handleOpenModal={handleOpenModal} />
                      </AccordionDetails>
                    </Accordion>
                    <Accordion
                      sx={{
                        background: palette.background.light,
                        color: theme.palette.text.dark,
                        border: `1px solid ${palette.primary.main}`,
                        boxShadow: "none",
                      }}
                    >
                      <AccordionSummary
                        expandIcon={
                          <ExpandMoreIcon
                            sx={{ color: palette.primary.main }}
                          />
                        }
                        sx={{
                          fontWeight: "bold",
                          background: activeEnvType
                            ? palette.primary.hover
                            : "transparent",
                        }}
                      >
                        {`Environment Risk ${
                          activeEnvType
                            ? `(${shadingLevel[activeLayerRef.current.name]})`
                            : ""
                        }`}
                      </AccordionSummary>
                      <AccordionDetails>
                        <EnvironmentalRiskReports
                          handleOpenModal={handleOpenModal}
                        />
                      </AccordionDetails>
                    </Accordion>
                  </AccordionDetails>
                </Accordion>

                <Accordion
                  defaultExpanded
                  sx={{
                    background: palette.background.light,
                    color: theme.palette.text.dark,
                    border: `1px solid ${palette.primary.main}`,
                    boxShadow: "none",
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreIcon sx={{ color: palette.primary.main }} />
                    }
                    sx={{ fontWeight: "bold" }}
                  >
                    Editing Tools
                  </AccordionSummary>
                  <AccordionDetails>
                    <MapEditingTools
                      mapRef={mapRef}
                      // showPolygonStyleSettings={() => setShowPolygonStyleSettings(true)}
                      setPolygonSelected={setPolygonSelected}
                      createPolygon={createPolygon}
                      setMapToolBarOptionFlags={setMapToolBarOptionFlags}
                      resetActiveId={resetActiveId}
                      resetMapToolbarOptionFlags={resetMapToolbarOptionFlags}
                      setShapeSettings={setShapeSettings}
                      resetAllOnceChanged={resetAllOnceChanged}
                    />
                  </AccordionDetails>
                </Accordion>

                <Accordion
                  defaultExpanded
                  sx={{
                    background: palette.background.light,
                    color: palette.text.dark,
                    border: `1px solid ${palette.primary.main}`,
                    boxShadow: "none",
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreIcon sx={{ color: palette.primary.main }} />
                    }
                    sx={{ fontWeight: "bold" }}
                  >
                    Map Styles
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      maxHeight: 130,
                      overflowY: "auto",
                      overflowX: "hidden",
                    }}
                  >
                    <MapStyles
                      mapRef={mapRef}
                      setShowOpacityControl={setShowOpacityControl}
                      resetAllOnceChanged={resetAllOnceChanged}
                    />
                  </AccordionDetails>
                </Accordion>

                <Accordion
                  sx={{
                    background: palette.background.light,
                    color: theme.palette.text.dark,
                    border: `1px solid ${palette.primary.main}`,
                    boxShadow: "none",
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreIcon sx={{ color: palette.primary.main }} />
                    }
                    sx={{ fontWeight: "bold" }}
                  >
                    Batch Upload
                  </AccordionSummary>
                  <AccordionDetails>
                    <BatchUpload />
                  </AccordionDetails>
                </Accordion>

                <Accordion
                  defaultExpanded
                  sx={{
                    background: palette.background.light,
                    color: theme.palette.text.dark,
                    border: `1px solid ${palette.primary.main}`,
                    boxShadow: "none",
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreIcon sx={{ color: palette.primary.main }} />
                    }
                    sx={{ fontWeight: "bold" }}
                  >
                    Additional Layers
                  </AccordionSummary>
                  <AccordionDetails>
                    <AdditionalLayers
                      mapRef={mapRef}
                      mapFeatureLayer={mapFeatureLayer}
                      mapRoadLayer={mapRoadLayer}
                    />
                  </AccordionDetails>
                </Accordion>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "flex-end",
                    position: "absolute",
                    bottom: 0,
                    // left: 0,
                    right: 23,
                    padding: "16px 0px",
                    background: palette.background.light,
                  }}
                >
                  <Button
                    sx={{
                      width: "140px",
                      borderRadius: "0px",
                      fontWeight: "bold",
                    }}
                    onClick={handleExportMenuClick}
                    variant="outlined"
                  >
                    <Box
                      component="span"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      Export
                      <PrintIcon fontSize="medium" sx={{ ml: 1 }} />
                    </Box>
                  </Button>
                </Box>
              </Box>
            </Box>
          </>
        ) : (
          <>
            <Accordion
              defaultExpanded
              sx={{
                background: palette.background.light,
                color: theme.palette.text.dark,
                border: `1px solid ${palette.primary.main}`,
                boxShadow: "none",
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ fontWeight: "bold" }}
              >
                Export Settings
              </AccordionSummary>
              <ExportSettings
                exportChanges={exportChanges}
                handleClose={handleCloseExport}
                exportSettingsData={exportSettingsData}
                setExportSettingsData={setExportSettingsData}
              />
            </Accordion>
          </>
        )}
      </Box>
      {isModalOpen && (
        <CrimeRiskSettingModal
          isOpen={isModalOpen}
          handleClose={handleCloseModal}
          onSave={HandleCrimeRiskSettingSave}
        />
      )}
    </>
  );
};

export default MapLayout;
